"use client";

import { cn } from "@/lib/utils";
import { type ComponentProps, memo, useEffect, useRef } from "react";
import { Streamdown } from "streamdown";
import rehypeRaw from "rehype-raw";
import { visit } from "unist-util-visit";
import type { Root } from "hast";

type ResponseProps = ComponentProps<typeof Streamdown> & {
  onSourceClick?: (
    example: { id: number; text: string },
    panel: "left" | "right"
  ) => void;
};

// Кастомный rehype плагин для удаления избыточных переносов строк
const rehypeRemoveExcessiveWhitespace = () => {
  return (tree: Root) => {
    visit(tree, "text", (node, index, parent) => {
      if (!parent || index === null || index === undefined) return;

      // Заменяем множественные переносы строк на пустую строку
      if (typeof node.value === "string") {
        // Удаляем более 1 последовательного переноса строки
        node.value = node.value.replace(/\n{2,}/g, "");

        // Если узел содержит только whitespace и переносы, и он между блочными элементами
        if (/^\s*$/.test(node.value)) {
          const siblings = parent.children;
          const prev = siblings[index - 1];
          const next = siblings[index + 1];

          // Если текстовый узел между двумя блочными элементами (например, таблицами)
          if (
            prev?.type === "element" &&
            next?.type === "element" &&
            ["table", "div", "section"].includes(prev.tagName) &&
            ["table", "div", "section"].includes(next.tagName)
          ) {
            // Заменяем на пустую строку
            node.value = "";
          }
        }
      }
    });
  };
};

export const Response = memo(
  ({ className, onSourceClick, ...props }: ResponseProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!onSourceClick || !containerRef.current) return;

      const container = containerRef.current;

      // Функция для обработки клика по цитате
      const handleQuoteClick = (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.tagName !== "CODE") return;

        // Проверяем, что это inline code (не внутри pre)
        const parentPre = target.closest("pre");
        if (parentPre) return;

        // Проверяем, что это число (цитата)
        const codeText = target.textContent?.trim();
        if (!codeText || !/^\d+$/.test(codeText)) return;

        const quoteNumber = parseInt(codeText, 10);
        const example = {
          id: quoteNumber,
          text: `Quote ${quoteNumber}`,
        };

        event.preventDefault();
        event.stopPropagation();
        onSourceClick(example, "left");
      };

      // Функция для добавления стилей к цитатам
      const styleQuotes = () => {
        const codeElements = container.querySelectorAll("code");
        const hoverHandlers: Array<{
          element: HTMLElement;
          enter: () => void;
          leave: () => void;
        }> = [];

        codeElements.forEach((code) => {
          // Пропускаем code внутри pre блоков
          if (code.closest("pre")) return;

          const codeText = code.textContent?.trim();

          // Применяем стили ко всем inline-code элементам
          code.style.display = "inline-flex";
          code.style.alignItems = "center";
          code.style.justifyContent = "center";
          code.style.boxSizing = "border-box";
          code.style.height = "20px";
          code.style.minWidth = "20px";
          code.style.width = "fit-content";
          code.style.padding = "1px 6px 0px 6px";
          code.style.lineHeight = "1.5";
          code.style.backgroundColor = "var(--tooltip-highlight-bg)";
          code.style.color = "var(--tooltip-highlight-text)";
          code.style.borderRadius = "24px";
          code.style.fontSize = "10px";
          code.style.fontWeight = "550";
          code.style.flexShrink = "0";
          code.style.transform = "translateY(0.0px)";
          code.style.fontFamily =
            '"Fira Code", "Monaco", "Cascadia Code", "Roboto Mono", monospace';
          code.style.userSelect = "none";
          code.style.transition = "background-color 0.2s, color 0.2s";

          // Проверяем, является ли это цитатой (числом) для добавления кликабельности
          if (codeText && /^\d+$/.test(codeText)) {
            code.style.cursor = "pointer";
            code.setAttribute("role", "button");
            code.setAttribute("tabindex", "0");
            code.setAttribute("aria-label", `Go to quote ${codeText}`);
          }

          // Создаем обработчики hover эффекта для всех inline-code
          const enterHandler = () => {
            code.style.backgroundColor = "var(--status-info-text)";
            code.style.color = "white";
          };
          const leaveHandler = () => {
            code.style.backgroundColor = "var(--tooltip-highlight-bg)";
            code.style.color = "var(--tooltip-highlight-text)";
          };

          code.addEventListener("mouseenter", enterHandler);
          code.addEventListener("mouseleave", leaveHandler);

          // Сохраняем ссылки для cleanup
          hoverHandlers.push({
            element: code,
            enter: enterHandler,
            leave: leaveHandler,
          });
        });

        return hoverHandlers;
      };

      // Добавляем обработчик клика
      container.addEventListener("click", handleQuoteClick);

      // Добавляем обработчик клавиатуры
      container.addEventListener("keydown", (event) => {
        const target = event.target as HTMLElement;
        if (target.tagName !== "CODE") return;

        const parentPre = target.closest("pre");
        if (parentPre) return;

        const codeText = target.textContent?.trim();
        if (!codeText || !/^\d+$/.test(codeText)) return;

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          handleQuoteClick(event);
        }
      });

      // Применяем стили и получаем обработчики для cleanup
      const hoverHandlers = styleQuotes();

      // Cleanup
      return () => {
        container.removeEventListener("click", handleQuoteClick);
        // Удаляем hover обработчики
        hoverHandlers.forEach(({ element, enter, leave }) => {
          element.removeEventListener("mouseenter", enter);
          element.removeEventListener("mouseleave", leave);
        });
      };
    }, [onSourceClick, props.children]);

    return (
      <div ref={containerRef}>
        <Streamdown
          className={cn(
            "size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
            // Стили для списков - правильное форматирование буллетов
            "[&_ul]:ml-4 [&_ol]:ml-4 [&_ul]:list-outside [&_ol]:list-outside",
            "[&_ul]:list-disc [&_ol]:list-decimal",
            "[&_li]:pl-2 [&_li]:whitespace-normal",
            // Стили для blockquote - переопределяем классы от Streamdown
            "[&_blockquote]:!my-3 [&_blockquote]:!px-3 [&_blockquote]:!py-0.5 [&_blockquote]:bg-transparent [&_blockquote]:rounded-sm [&_blockquote]:!border-l-2 [&_blockquote]:!border-l-[var(--status-info-text)]",
            // Стили для параграфов - уменьшаем расстояние между строками
            // "[&_p]:!leading-tight",
            // Исключаем параграфы внутри blockquote из всех отступов
            "[&_blockquote_p]:!leading-tight [&_blockquote_p]:!py-0 [&_blockquote_p]:!my-0 [&_blockquote_p]:!mb-0 [&_blockquote_p]:!mt-0",
            // Стили для таблиц - добавляем скругление, бордеры и выравнивание
            "[&_table]:rounded-lg [&_table]:overflow-hidden [&_table]:border [&_table]:border-border [&_table]:bg-background",
            "[&_th]:border [&_th]:border-border [&_th]:px-4 [&_th]:py-2 [&_th]:text-left [&_th]:align-top",
            "[&_td]:border [&_td]:border-border [&_td]:px-4 [&_td]:py-2 [&_td]:align-top",
            // Скрываем блок с иконками копирования и скачивания в table-wrapper
            '[&_div[data-streamdown="table-wrapper"]_>_div.flex.items-center.justify-end.gap-1]:hidden',
            className
          )}
          rehypePlugins={[rehypeRaw, rehypeRemoveExcessiveWhitespace]}
          {...props}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // If custom markdown renderers are provided, always re-render.
    // They often capture interactive state (e.g. hover popovers).
    if (prevProps.components || nextProps.components) {
      return false;
    }

    return (
      prevProps.children === nextProps.children &&
      prevProps.className === nextProps.className &&
      prevProps.onSourceClick === nextProps.onSourceClick &&
      prevProps.parseIncompleteMarkdown === nextProps.parseIncompleteMarkdown
    );
  }
);

Response.displayName = "Response";
